// src/components/panels/CurrentConditions.jsx
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Thermometer, Droplets, Wind, Sun, Activity } from 'lucide-react';

const MetricCard = ({ title, value, unit, icon: Icon, trend = null, color = "text-gray-600" }) => (
  <div className="bg-white p-4 rounded-lg shadow">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <Icon className={`h-5 w-5 ${color}`} />
        <span className="text-sm text-gray-500">{title}</span>
      </div>
      {trend && (
        <span className={`text-sm ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}
        </span>
      )}
    </div>
    <div className="mt-2 text-2xl font-bold">
      {typeof value === 'number' ? value.toFixed(1) : value} {unit}
    </div>
  </div>
);

const CurrentConditions = ({ data, processedData, units, convertValue }) => {
  const current = data[data.length - 1];
  const previous = data[data.length - 2];
  
  const getTrend = (current, previous, metric) => {
    if (!previous) return null;
    return current[metric] - previous[metric];
  };

  const getComfortColor = (comfortLevel) => {
    const colors = {
      'Extreme Danger': 'text-red-600',
      'Danger': 'text-red-500',
      'Caution': 'text-yellow-500',
      'Cool': 'text-blue-500',
      'Comfortable': 'text-green-500'
    };
    return colors[comfortLevel] || 'text-gray-600';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Current Conditions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetricCard
            title="Temperature"
            value={convertValue(current.temperature, 'temperature')}
            unit={units.temperature === 'C' ? '°C' : '°F'}
            icon={Thermometer}
            trend={getTrend(current, previous, 'temperature')}
          />
          
          <MetricCard
            title="Humidity"
            value={current.humidity}
            unit="%"
            icon={Droplets}
            trend={getTrend(current, previous, 'humidity')}
          />
          
          <MetricCard
            title="Pressure"
            value={convertValue(current.pressure, 'pressure')}
            unit={units.pressure === 'hPa' ? 'hPa' : 'inHg'}
            icon={Wind}
            trend={getTrend(current, previous, 'pressure')}
          />
          
          <MetricCard
            title="Heat Index"
            value={convertValue(current.heatIndex, 'temperature')}
            unit={units.temperature === 'C' ? '°C' : '°F'}
            icon={Sun}
            color={getComfortColor(processedData.comfortLevel.level)}
          />
          
          <MetricCard
            title="Dew Point"
            value={convertValue(current.dewPoint, 'temperature')}
            unit={units.temperature === 'C' ? '°C' : '°F'}
            icon={Droplets}
          />
          
          <MetricCard
            title="Comfort Level"
            value={processedData.comfortLevel.level}
            unit=""
            icon={Activity}
            color={getComfortColor(processedData.comfortLevel.level)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrentConditions;